import * as React from 'react';
import changeTitleForClearingRedDot from 'mini-common-utils/lib/utils/changeTitleForClearingRedDot';
import folderStore from 'mini-mail-folder-store/lib/store/Store';
import getIsInCheckedMode from 'mini-mail-list-view/lib/utils/getIsInCheckedMode';
import LazyItemReadingPaneHost from 'mini-mail-item-reading-pane-host';
import ListPane from 'mini-mail-list-view/lib/components/ListPane';
import MailHeader from 'mini-mail-header-view/lib/components/MailHeader';
import mailStore from 'owa-mail-store/lib/store/Store';
import Module from 'mini-module-switcher/lib/schema/Module';
import ModuleSwitcher from 'mini-module-switcher/lib/components/ModuleSwitcher';
import sendRenderCompleteMessageToExtension from 'mini-mail-bootstrap/lib/utils/sendRenderCompleteMessageToExtension';
import switchModule from 'owa-mail-store/lib/actions/switchModule';
import { BrowserRouter as Router } from 'react-router-dom';
import { ComposeSource } from 'mini-mail-compose-store/lib/store/schema/ComposeSource';
import { composeStore } from 'mini-mail-compose-store';
import { FeedbackForm } from 'mini-feedback';
import { FolderPane } from 'mini-mail-folder-view';
import { getHasECLoaded } from 'mini-email-context/lib/getHasECLoaded';
import { getMailPath } from 'owa-url';
import { getMailRouteStore } from 'mini-mail-route-store/lib/store/MailRouteStore';
import { isInSearchView } from 'mini-mail-list-store/lib/selectors/isInSearchView';
import { lazyDeleteComposeDraft } from 'mini-mail-compose-actions-mail';
import { logUsage } from 'owa-analytics';
import { MAIL_MODULE_SWITCHER_ICON_CLICKED, mailEventEmitter } from 'mini-mail-event-emitters';
import { MailCompose } from 'mini-mail-compose-view';
import { MailModuleDiagnostics } from 'mini-mail-diagnostics';
import { MailRouteType } from 'mini-mail-route-store/lib/schema/MailRouteType';
import { observer } from 'mobx-react';
import { OptionsPage } from 'mini-options-view';
import { PeopleCardModuleWrapper } from 'mini-people-card-module-wrapper';
import { PeopleCardRouteStore } from 'mini-people-card-route-store';
import { ReadingPane } from 'mini-mail-reading-pane-view';
import { SearchModule } from 'mini-search-landing-view';
import { SurveyWrapper } from 'mini-survey';
import { targetedAdV3Dismissed } from 'mini-gdprv3-ads-opt-in/lib/utils/targetedAdUtils';
import { trace } from 'owa-trace';

const styles = require('./MailModule.scss');
const classNames = require('classnames/bind').bind(styles);

function removeOutlineSuppressorClass() {
    const ElsWithSuppressedOutline = document.getElementsByClassName('suppressBoxShadow');
    if (ElsWithSuppressedOutline.length) {
        Array.from(ElsWithSuppressedOutline).forEach(el => {
            el.classList.remove('suppressBoxShadow');
        });
    }
}

@observer
export default class MailModule extends React.Component<{}, {}> {
    private renderReadingPaneContainer(
        selectedId: string,
        isComposeShownOnTop: boolean
    ): JSX.Element {
        const readingPaneContainerClasses = classNames('readingPaneContainer', {
            'readingPaneContainer--shown': selectedId,
        });
        return (
            <div aria-hidden={isComposeShownOnTop} className={readingPaneContainerClasses}>
                {selectedId && <ReadingPane />}
            </div>
        );
    }

    componentDidMount() {
        sendRenderCompleteMessageToExtension();
        document.addEventListener('keyup', removeOutlineSuppressorClass);
        document.body.addEventListener('dragover', this.handleDragEvent);
        changeTitleForClearingRedDot();
    }

    private handleDragEvent(ev: DragEvent) {
        ev.preventDefault();
        ev.stopPropagation();
    }

    componentWillUnmount() {
        document.removeEventListener('keyup', removeOutlineSuppressorClass);
        document.body.removeEventListener('dragover', this.handleDragEvent);
    }

    /**
     * Return the component to be rendered based on the module selected.
     * TODO: Optimise this rendering as part of the react-router changes.
     * Each module should correspond to unique route
     */
    private getMailComponentToRender(): JSX.Element {
        let component: JSX.Element = null;

        switch (mailStore.mailModule.selectedModule) {
            case Module.Mail:
                component = this.renderMailModule();
                break;
            case Module.Search:
                component = this.renderSearchModule();
                break;
            case Module.Feedback:
                component = this.renderFeedbackModule();
                break;
            case Module.Options:
                component = this.renderOptionsModule();
                break;
            case Module.Survey:
                component = this.renderSurveyModule();
                break;
            case Module.Diagnostics:
                component = this.renderDiagnosticsModule();
                break;
            default:
                trace.warn('Invalid Module encountered.');
                component = null;
                break;
        }
        return component;
    }

    render() {
        const { routeType } = getMailRouteStore();

        // Determine the component to be rendered based on the routeType mini-mail-route-store.
        // Covers following distinct cases,
        // - reading pane deep-link scenario
        // - mail scenarios like compose, reading pane etc.
        const getChildren = () => {
            switch (routeType) {
                case MailRouteType.deeplinkReadItem:
                    return <LazyItemReadingPaneHost />;
                default:
                    return this.getMailComponentToRender();
            }
        };

        return <Router basename={getMailPath()}>{getChildren()}</Router>;
    }

    private renderSearchModule(): JSX.Element {
        return <SearchModule />;
    }

    private renderMailModule(): JSX.Element {
        const { selectedId } = mailStore.mailModule;
        const shouldRenderModuleSwitcher =
            !getIsInCheckedMode() &&
            !composeStore.defaultViewState &&
            !selectedId &&
            !isInSearchView();
        const { currentRoute, peopleCardMountProps } = PeopleCardRouteStore;

        const shouldShowMailComposeView =
            composeStore.defaultViewState &&
            composeStore.defaultViewState.source === ComposeSource.Mail;

        const renderCoreMailList = (
            <div id="mailModuleFocusElement" tabIndex={-1}>
                {(folderStore.showFolderPane || folderStore.showMoveToFolderPane) && <FolderPane />}

                <MailHeader />

                <ListPane />

                {this.renderReadingPaneContainer(selectedId, shouldShowMailComposeView)}

                {shouldShowMailComposeView && (
                    <MailCompose customComposeDraftDelete={this.onComposeDraftDelete} />
                )}
                {shouldRenderModuleSwitcher && this.renderModuleSwitcher()}

                {!!currentRoute && !!currentRoute.routeIdType && (
                    <PeopleCardModuleWrapper emailAddress={peopleCardMountProps.emailAddress} />
                )}
            </div>
        );

        return getHasECLoaded() && renderCoreMailList;
    }

    private onComposeDraftDelete = async (itemId: string) => {
        (await lazyDeleteComposeDraft.import())(itemId);
    };

    private renderFeedbackModule(): JSX.Element {
        return (
            <div>
                <FeedbackForm
                    containerModule={Module.Mail}
                    onCloseCallback={this.switchBackToMailModule}
                />
            </div>
        );
    }

    private renderOptionsModule(): JSX.Element {
        return <OptionsPage />;
    }

    private renderSurveyModule(): JSX.Element {
        return <SurveyWrapper />;
    }

    private switchBackToMailModule = () => {
        targetedAdV3Dismissed();
        switchModule(Module.Mail);
    };

    private renderModuleSwitcher(): JSX.Element {
        const calendarIconRef = React.createRef<HTMLElement>();
        return (
            <>
                <ModuleSwitcher
                    currentModule={Module.Mail}
                    iconRefs={{ calendarIconRef }}
                    onActiveModuleClickAction={this.onActiveModuleClickAction}
                />
            </>
        );
    }

    private onActiveModuleClickAction() {
        mailEventEmitter.emit(MAIL_MODULE_SWITCHER_ICON_CLICKED, null);
        logUsage('ScrollToTopOnReClick', { source: 'MailModule' });
    }

    private renderDiagnosticsModule(): JSX.Element {
        return <MailModuleDiagnostics onDismiss={() => switchModule(Module.Mail)} />;
    }
}
